import {
  Box,
  Stack,
  HStack,
  Heading,
  Text,
  Button,
  Tag,
  Grid,
  Flex,
  Wrap,
  WrapItem,
  useColorMode,
} from '@chakra-ui/react';
import ComponentLayout from '../layouts/ComponentLayout';
import { redirectToGmail } from '../sections/Footer';
import { FlipWords } from '../ui-aceternity/flip-words';
import { EngineeringJourney } from './EngineeringJourney';

const heroFocusWords = [
  'Backend systems',
  'Payment flows',
  'API integrations',
  'Digital products',
];

const stats = [
  {
    value: '9+ years',
    label:
      'building production software across SaaS, fintech and digital products',
  },
  {
    value: 'End-to-end',
    label:
      'from APIs, integrations and data systems to user-facing web applications',
  },
  {
    value: 'Collaboration',
    label:
      'working with product, engineering and business teams across the UK, Europe, the US and Australia',
  },
];

const focusAreas = [
  {
    title: 'Backend',
    items: ['TypeScript', 'Go', 'REST', 'gRPC'],
    accent: false,
  },
  {
    title: 'Data and systems',
    items: ['PostgreSQL', 'Kafka', 'API integrations', 'Event-driven systems'],
    accent: false,
  },
  {
    title: 'Full-stack',
    items: ['React', 'Next.js', 'React Native'],
    accent: false,
  },
  {
    title: 'Cloud and delivery',
    items: ['AWS', 'Docker', 'Kubernetes', 'CI/CD'],
    accent: false,
  },
  {
    title: 'Domains',
    items: [
      'Fintech & Payments',
      'SaaS',
      'Real Estate Technology',
      'Digital Assets',
      'Consumer Products',
    ],
    accent: true,
  },
];

function Pill({ children, colorMode }) {
  return (
    <Tag
      alignSelf="flex-start"
      bg={`mode.${colorMode}.accentBG`}
      color={`mode.${colorMode}.accent`}
      fontWeight="bold"
      fontSize="xs"
      letterSpacing="wide"
      px={3}
      py={1}
      rounded="sm"
    >
      {children}
    </Tag>
  );
}

function StatCell({ value, label, colorMode, showBorder }) {
  return (
    <Box
      flex="1"
      borderColor={`mode.${colorMode}.border`}
      borderBottomWidth={{ base: showBorder ? '1px' : 0, md: 0 }}
      borderRightWidth={{ base: 0, md: showBorder ? '1px' : 0 }}
      px={{ base: 0, md: 8 }}
      py={6}
    >
      <Heading
        fontSize={{ base: '3xl', md: '4xl' }}
        color={`mode.${colorMode}.text`}
      >
        {value}
      </Heading>
      <Text mt={2} fontSize="sm" color={`mode.${colorMode}.subtext`}>
        {label}
      </Text>
    </Box>
  );
}

function FullHome() {
  const { colorMode } = useColorMode();

  return (
    <ComponentLayout title="Full home page">
      <Stack
        as="main"
        spacing={{ base: 12, md: 16 }}
        w="100%"
        maxW="1100px"
        mx="auto"
      >
        <Stack spacing={5} maxW="720px">
          <Pill colorMode={colorMode}>Senior Software Engineer</Pill>
          <Stack spacing={2}>
            <Heading
              as="h1"
              color={`mode.${colorMode}.text`}
              fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
              lineHeight="1.15"
              fontWeight="bold"
            >
              From APIs to pixels, I make complicated things feel simple.
            </Heading>
            <Box
              minH={{ base: 7, md: 8 }}
              color={`mode.${colorMode}.accent`}
              fontSize={{ base: 'md', md: 'lg' }}
              fontWeight="bold"
            >
              <FlipWords
                words={heroFocusWords}
                className="px-0 text-inherit dark:text-inherit"
              />
            </Box>
          </Stack>
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            color={`mode.${colorMode}.subtext`}
          >
            I&apos;m a product-focused full-stack engineer with experience
            building production software across fintech, SaaS, real estate and
            digital products.
          </Text>
          <HStack spacing={4} pt={2} flexWrap="wrap">
            <Button
              as="a"
              href="/projects"
              bg={`mode.${colorMode}.accent`}
              color="white"
              fontWeight="bold"
              rounded="md"
              _hover={{ opacity: 0.9, textDecoration: 'none' }}
            >
              View my work
            </Button>
            <Button
              as="a"
              href={redirectToGmail()}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              borderColor={`mode.${colorMode}.border`}
              color={`mode.${colorMode}.text`}
              fontWeight="bold"
              rounded="md"
              _hover={{
                textDecoration: 'none',
                bg: `mode.${colorMode}.cardBG`,
              }}
            >
              Get in touch
            </Button>
          </HStack>
        </Stack>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          borderTopWidth="1px"
          borderBottomWidth="1px"
          borderColor={`mode.${colorMode}.border`}
        >
          {stats.map((stat, index) => (
            <StatCell
              key={stat.value}
              value={stat.value}
              label={stat.label}
              colorMode={colorMode}
              showBorder={index < stats.length - 1}
            />
          ))}
        </Flex>

        <Grid
          templateColumns={{ base: '1fr', md: '1.1fr 0.9fr' }}
          gap={{ base: 8, md: 10 }}
        >
          <Stack spacing={4}>
            <Pill colorMode={colorMode}>About</Pill>
            <Heading
              color={`mode.${colorMode}.text`}
              fontSize={{ base: '2xl', md: '3xl' }}
            >
              Product-focused, full-stack when the product needs it.
            </Heading>
            <Text color={`mode.${colorMode}.subtext`}>
              I build{' '}
              <Text
                as="span"
                color={`mode.${colorMode}.accent`}
                fontWeight="bold"
              >
                products
              </Text>
              , not just code. I ask the hard questions early to ensure we are
              solving problems that matter. While anyone can write code, my
              focus is on finding the right problem to solve.{' '}
            </Text>
            <Text color={`mode.${colorMode}.subtext`}>
              I also love collaborating across departments. Teamwork is what
              makes engineering fun.
            </Text>
            <Text color={`mode.${colorMode}.subtext`}>
              When I'm not at my desk, I love staying active. You'll usually
              find me running 🏃‍♂️, hiking 🏔️, padelling 🎾, or travelling ✈️.
            </Text>
          </Stack>

          <Box
            bg={`mode.${colorMode}.cardBG`}
            border="1px solid"
            borderColor={`mode.${colorMode}.border`}
            rounded="md"
            p={6}
          >
            <Text
              fontSize="xs"
              fontWeight="bold"
              letterSpacing="wide"
              color={`mode.${colorMode}.accent`}
              mb={4}
            >
              FOCUS AREAS
            </Text>
            <Stack spacing={5}>
              {focusAreas.map((area) => (
                <Box key={area.title}>
                  <Text
                    fontWeight="bold"
                    color={`mode.${colorMode}.text`}
                    mb={2}
                  >
                    {area.title}
                  </Text>
                  <Wrap spacing={2}>
                    {area.items.map((item) => (
                      <WrapItem key={item}>
                        <Tag
                          size="sm"
                          fontWeight="semibold"
                          bg={
                            area.accent
                              ? `mode.${colorMode}.accentBG`
                              : `mode.${colorMode}.career.tagBG`
                          }
                          color={
                            area.accent
                              ? `mode.${colorMode}.accent`
                              : `mode.${colorMode}.career.subtext`
                          }
                          border={area.accent ? '1px solid' : 'none'}
                          borderColor={`mode.${colorMode}.accentBorder`}
                        >
                          {item}
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>

        <EngineeringJourney />
      </Stack>
    </ComponentLayout>
  );
}

export default FullHome;
